import '../../text-and-lang/as-text.ts';
import '../../overlays/as-tooltip-wrapper.ts';
import styleText from './as-text-button.css?raw';
import { AsButtonBase } from './as-button-base.ts';

/**
 * # AsTextButton Component
 * 
 * A text button component that provides a consistent UI for text buttons
 * across the application suite.
 * 
 * ## Features
 * - Displays text content with consistent styling
 * - Hover effects for better user interaction
 * - Emits events when clicked
 * - Fully customizable via attributes and CSS custom properties
 * - Supports both direct text content and localized text via dict-key
 * - Automatically wraps any innerText in as-text component
 * 
 * ## Component Hierarchy
 * ```
 * AsTextButton (as-text-button)
 * ├── button - The clickable button element
 * │   └── as-text - The text component for displaying button text
 * └── as-tooltip - Tooltip components for responses and info (optional)
 * ```
 * 
 * ## Attributes
 * - `button-id`: Optional ID for the button element
 * - `default-text`: Text content to display in the button (overrides innerText)
 * - `dict-key`: Optional key for localized text
 * - `name`: Optional name for the button, used for event identification
 * - `tooltip-default-text`: Default text content for the tooltip
 * - `tooltip-dict-key`: Localization key for tooltip text
 * - `tooltip-placement`: Position of the tooltip (top, right, bottom, left, top-left, top-right, bottom-left, bottom-right, etc.)
 * - `tooltip-auto-flip`: When present, allows the tooltip to flip position when near viewport edges
 * 
 * ## CSS Custom Properties
 * - `--text-btn-padding`: All-sides padding - when set, overrides x/y specific padding (default: unset)
 * - `--text-btn-padding-x`: Horizontal padding (default: 1rem, ignored if --text-btn-padding is set)
 * - `--text-btn-padding-y`: Vertical padding (default: 0.5rem, ignored if --text-btn-padding is set)
 * - `--text-btn-radius`: Border radius (default: 0.5rem, matches icon button)
 * - `--text-btn-size`: Button height (default: 100%, fills parent height)
 * - `--text-btn-bg-col`: Background color (default: rgba(224, 224, 224, 0.3), translucent overlay)
 * - `--text-btn-hover-bg-col`: Hover background color (default: #002738, matches icon button)
 * - `--text-btn-text-col`: Text color (default: #333333)
 * - `--text-btn-hover-text-col`: Text hover color (default: #ffffff)
 * - `--text-btn-font-size`: Font size (default: 1rem)
 * - `--text-btn-font-weight`: Font weight (default: normal)
 * - `--text-btn-min-width`: Minimum width (default: auto)
 * - `--text-btn-max-width`: Maximum width (default: none)
 * - `--text-btn-width`: Width (default: 100%, fills parent width)
 * - `--text-btn-transition`: Transition duration (default: background-color 0.2s ease, color 0.2s ease)
 * - `--text-btn-focus-outline`: General focus outline (default: none - no outline on mouse click)
 * - `--text-btn-focus-outline-offset`: General focus outline offset (default: 2px)
 * - `--text-btn-focus-box-shadow`: General focus box shadow (default: none)
 * - `--text-btn-focus-visible-outline`: Keyboard focus outline (default: 2px solid #0066cc)
 * - `--text-btn-focus-visible-outline-offset`: Keyboard focus outline offset (default: 2px)
 * - `--text-btn-focus-visible-box-shadow`: Keyboard focus box shadow (default: none)
 * - `--text-btn-focus-outline-col`: Focus outline color (default: #0066cc)
 * - `--text-btn-active-transform`: Active state transform (default: translateY(1px))
 * - `--text-btn-disabled-opacity`: Disabled state opacity (default: 0.6)
 * 
 * ## Legacy CSS Custom Properties (for backward compatibility)
 * - `--button-padding-y`: Maps to --text-btn-padding (vertical padding)
 * - `--button-padding-x`: Maps to --text-btn-padding-x
 * - `--button-radius`: Maps to --text-btn-radius
 * - `--button-height`: Maps to --text-btn-size
 * - `--button-bg-color`: Maps to --text-btn-bg-col
 * - `--button-hover-bg-color`: Maps to --text-btn-hover-bg-col
 * - `--text-color`: Maps to --text-btn-text-col
 * - `--button-hover-text-color`: Maps to --text-btn-hover-text-col
 * 
 * ## Icon Button Compatibility
 * - `--icon-btn-padding`: Maps to --text-btn-padding
 * - `--icon-btn-radius`: Maps to --text-btn-radius
 * - `--icon-btn-size`: Maps to --text-btn-size
 * - `--icon-btn-hover-bg-col`: Maps to --text-btn-hover-bg-col
 * 
 * ## Events
 * ### Emitted Events
 * - `as-button-click`: Dispatched when the button is clicked
 *   - Detail: `{ buttonName: string | null }` - The name of the button if set
 * 
 * ## Usage Examples
 * 
 * ### Basic Usage
 * ```html
 * <as-text-button default-text="Click Me"></as-text-button>
 * ```
 * 
 * ### With innerText (automatically wrapped in as-text)
 * ```html
 * <as-text-button>Click Me</as-text-button>
 * ```
 * 
 * ### With Custom Name
 * ```html
 * <as-text-button default-text="Submit" name="submit-button"></as-text-button>
 * ```
 * 
 * ### With Localized Text
 * ```html
 * <as-text-button dict-key="common.submit" default-text="Submit"></as-text-button>
 * ```
 * 
 * ### With Tooltip
 * ```html
 * <as-text-button default-text="Save" tooltip-default-text="Save your work"></as-text-button>
 * ```
 * 
 * ### Handling Click Events
 * ```javascript
 * const textButton = document.querySelector('as-text-button');
 * textButton.addEventListener('as-button-click', (e) => {
 *   console.log('Button clicked!', e.detail.buttonName);
 * });
 * ```
 */

// Template function for rendering the button content
const renderButtonContent = (text: string | null, dictKey: string | null) => {
  if (dictKey) {
    return `<as-text dict-key="${dictKey}" default-text="${text || ''}"></as-text>`;
  } else if (text) {
    return `<as-text>${text}</as-text>`;
  }
  return '';
};



export class AsTextButton extends AsButtonBase {
  static get observedAttributes() {
    return ['button-id', 'link', 'default-text', 'dict-key', 'name', 'tooltip-default-text', 'tooltip-dict-key', 'tooltip-placement', 'tooltip-auto-flip', 'disable-tooltips', 'disabled'];
  }

  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  private textObserver: MutationObserver | null = null;

  private getDisplayText(): string | null {
    const defaultText = this.getAttribute('default-text');
    const innerText = this.textContent?.trim();

    // Priority: default-text > innerText
    return defaultText ?? innerText ?? null;
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsButtonBase._baseSheet, AsTextButton.sheet];
    this.render();
  }

  /**
   * Render the component
   */
  private render() {
    const buttonId = this.getAttribute('button-id') || '';
    const displayText = this.getDisplayText();
    const dictKey = this.getAttribute('dict-key');

    // Build tooltip wrapper attributes
    const tooltipAttrs = [];
    const hasTooltipContent = this.getAttribute('tooltip-dict-key') || this.getAttribute('tooltip-default-text');
    const tooltipsDisabled = this.hasAttribute('disable-tooltips');

    if (this.getAttribute('tooltip-dict-key')) {
      tooltipAttrs.push(`dict-key="${this.getAttribute('tooltip-dict-key')}"`);
    }
    if (this.getAttribute('tooltip-default-text')) {
      tooltipAttrs.push(`default-text="${this.getAttribute('tooltip-default-text')}"`);
    }
    if (this.getAttribute('tooltip-placement')) {
      tooltipAttrs.push(`placement="${this.getAttribute('tooltip-placement')}"`);
    }
    if (this.getAttribute('tooltip-auto-flip') !== null) {
      tooltipAttrs.push('auto-flip');
    }

    // Add show-on-hover if there's tooltip content and tooltips are not disabled
    if (hasTooltipContent && !tooltipsDisabled) {
      tooltipAttrs.push('show-on-hover');
    }

    const isDisabled = this.hasAttribute('disabled');
    const buttonHTML = `<button id="${buttonId}"${isDisabled ? ' disabled' : ''}>
      ${renderButtonContent(displayText, dictKey)}
    </button>`;

    this.shadowRoot!.innerHTML = /*html*/`
      <as-tooltip-wrapper ${tooltipAttrs.join(' ')}>
        ${buttonHTML}
      </as-tooltip-wrapper>
    `;
  }

  private updateSkeletonClass() {
    const displayText = this.getDisplayText();
    const dictKey = this.getAttribute('dict-key');
    if (!displayText && !dictKey) {
      this.classList.add('skeleton');
    } else {
      this.classList.remove('skeleton');
    }
  }

  connectedCallback() {
    this.updateSkeletonClass();
    this.setupTextObserver();
    // Set up event listener once at the root level using event delegation
    this.shadowRoot!.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    // Clean up the text observer
    if (this.textObserver) {
      this.textObserver.disconnect();
      this.textObserver = null;
    }
    // Clean up event listener
    this.shadowRoot?.removeEventListener('click', this.handleClick);
  }

  /**
   * Set up a MutationObserver to watch for changes to text content
   */
  private setupTextObserver() {
    // Clean up existing observer
    if (this.textObserver) {
      this.textObserver.disconnect();
    }

    // Create new observer to watch for text changes
    this.textObserver = new MutationObserver((mutations) => {
      let shouldRerender = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          // Text content has changed, check if we need to re-render
          const currentDisplayText = this.getDisplayText();
          const buttonText = this.shadowRoot?.querySelector('button as-text')?.textContent?.trim();

          if (currentDisplayText !== buttonText) {
            shouldRerender = true;
          }
        }
      });

      if (shouldRerender) {
        this.render();
      }
    });

    // Start observing changes to text content
    this.textObserver.observe(this, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  /**
   * Handle click events on the button using event delegation
   */
  private handleClick = (e: Event) => {
    const target = e.target as HTMLElement;
    // Check if the click was on the button or its children
    if (target.tagName === 'BUTTON' || target.closest('button')) {
      // console.log("dispatching as-button-click on text button:", this.getAttribute('name') ?? this.getAttribute('button-id'))
      this._dispatchButtonClick({
        buttonName: this.getAttribute('name') ?? this.getAttribute('button-id'),
      });

      if(this.getAttribute("link")?.length>0){
        const href = this.getAttribute("link")
        const a = document.createElement("a")
        a.href = href
        if(this.hasAttribute("link-new-tab")) a.target = "_blank"
        a.click()
      }
    }
  }

  /**
   * Show a response tooltip by temporarily updating the wrapper's tooltip content
   */
  showResponse = (data: { textKey: string, defaultText: string }) => {
    const wrapper = this.shadowRoot?.querySelector('as-tooltip-wrapper');
    const tooltip = wrapper?.querySelector('as-tooltip');

    if (tooltip && wrapper) {
      // Add response class for styling
      wrapper.classList.add('response');

      // Update the tooltip content
      const textElement = tooltip.querySelector('as-text');
      if (textElement) {
        if (data.textKey) {
          textElement.setAttribute('dict-key', data.textKey);
        }
        textElement.textContent = data.defaultText;
      }

      // Show the tooltip
      tooltip.setAttribute('show', '');

      // Hide after 3 seconds and restore original content
      setTimeout(() => {
        tooltip.removeAttribute('show');
        wrapper.classList.remove('response');

        // Restore original content
        setTimeout(() => {
          if (textElement) {
            const originalDictKey = this.getAttribute('tooltip-dict-key');
            const originalText = this.getAttribute('tooltip-default-text');

            if (originalDictKey) {
              textElement.setAttribute('dict-key', originalDictKey);
            } else {
              textElement.removeAttribute('dict-key');
            }
            textElement.textContent = originalText || '';
          }
        }, 300); // Small delay to let hide animation complete
      }, 3000);
    }
  }

  /**
   * Respond to attribute changes
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (!this.shadowRoot) return;

    this.render();
    if (this.isConnected) this.updateSkeletonClass();
  }
}

// Register the text button component
customElements.define('as-text-button', AsTextButton); 
